import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  AssetReviewCommentsControllerGetAssetCommentsParams,
  AssetReviewControllerCountAssetReviewsParams,
  AssetReviewControllerDeleteAssetReviewParams,
  AssetReviewControllerGetAssetReviewParams,
  AssetReviewControllerGetReviewContentParams,
  AssetReviewControllerListAssetReviewsParams,
  AssetReviewItemDto,
  GetAssetVersionCommentsSuccessDto,
  CreateAssetReviewDto,
  ShareAssetReviewLinkDTO,
  UpdateAssetReviewDto
} from '@api/Api';
import { QUERY_COMMENTS_ASSETS } from '@hooks/queries/asset';
import {
  assetReviewDeleteAssetReview,
  assetReviewGetAssetReview,
  createAssetReview,
  getAssetReviewContent,
  getAssetReviewCount,
  getCommentsAssetReview,
  listAssetReviews,
  shareAssetReviewLink,
  updateAssetReview
} from '@api/AssetReview';
import {
  getPlaceholderQuery,
  optimisticListItemRemove,
  optimisticListItemUpdate
} from '@hooks/queries/helpers';

export const QUERY_ASSET_REVIEWS_LIST = 'asset_reviews_list';
export const QUERY_ASSET_REVIEW = 'asset_review';
export const QUERY_ASSET_REVIEW_CONTENT = 'asset_review_content';
export const QUERY_ASSET_REVIEW_COUNT = 'asset_review_count';

export function useCommentsAssetReviewQuery(
  params: AssetReviewCommentsControllerGetAssetCommentsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: GetAssetVersionCommentsSuccessDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: [QUERY_COMMENTS_ASSETS, params.assetVersionId, params],
    queryFn: async ({ signal }) => {
      const { data } = await getCommentsAssetReview(params, {
        signal
      });
      return data;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      return getPlaceholderQuery<GetAssetVersionCommentsSuccessDto>(
        [QUERY_COMMENTS_ASSETS, params.assetVersionId],
        (data): data is GetAssetVersionCommentsSuccessDto =>
          !!data &&
          typeof data === 'object' &&
          Array.isArray((data as GetAssetVersionCommentsSuccessDto).comments)
      );
    }
  });
}

export function useListAssetReviewsQuery({
  params,
  enabled = true
}: {
  params: AssetReviewControllerListAssetReviewsParams;
  enabled?: boolean;
}) {
  const fetch = useInfiniteQuery({
    enabled: !!enabled && !!params.campaignId,
    queryKey: [QUERY_ASSET_REVIEWS_LIST, params],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await listAssetReviews(
        {
          ...params,
          after: pageParam
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });

  return fetch;
}

export function useAssetReviewQuery({
  params,
  enabled = true
}: {
  params: AssetReviewControllerGetAssetReviewParams;
  enabled?: boolean;
}) {
  return useQuery({
    enabled: !!enabled && !!params.reviewId,
    queryKey: [QUERY_ASSET_REVIEW, params.reviewId],
    queryFn: async ({ signal }) => {
      const { data } = await assetReviewGetAssetReview(params, {
        signal
      });
      return data;
    }
  });
}

export const useAssetReviewContentQuery = ({
  params,
  enabled = true
}: {
  params: AssetReviewControllerGetReviewContentParams;
  enabled?: boolean;
}) => {
  return useQuery({
    enabled: !!enabled && !!params.reviewId,
    queryKey: [
      QUERY_ASSET_REVIEW_CONTENT,
      params.reviewId,
      params.password ?? null
    ],
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await getAssetReviewContent(params, { signal });
      return data;
    }
  });
};

export const useAssetReviewCountQuery = ({
  params,
  enabled = true,
  keepPreviousData = false
}: {
  params: AssetReviewControllerCountAssetReviewsParams;
  enabled?: boolean;
  keepPreviousData?: boolean;
}) => {
  return useQuery({
    enabled: !!enabled && !!params.campaignId,
    keepPreviousData,
    queryKey: [QUERY_ASSET_REVIEW_COUNT, params.campaignId],
    queryFn: async ({ signal }) => {
      const { data } = await getAssetReviewCount(params, { signal });
      return data.count || 0;
    }
  });
};

export function useCreateAssetReviewMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateAssetReviewDto) => {
      return await createAssetReview(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_ASSET_REVIEWS_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_ASSET_REVIEW_COUNT] });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });
  return mutation;
}

export function useShareAssetReviewLinkMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: ShareAssetReviewLinkDTO) => {
      return await shareAssetReviewLink(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });
  return mutation;
}

export function useUpdateAssetReviewMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: UpdateAssetReviewDto) => {
      return await updateAssetReview(data);
    },
    onMutate: (data) => {
      optimisticListItemUpdate<AssetReviewItemDto>(
        {
          queryKey: [QUERY_ASSET_REVIEWS_LIST],
          id: data.reviewId,
          predicate: (query) => {
            return query.queryKey.includes(QUERY_ASSET_REVIEWS_LIST);
          }
        },
        (draft) => {
          Object.entries(data).forEach(([key, value]) => {
            if (value !== undefined) {
              (draft as any)[key] = value;
            }
          });
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_ASSET_REVIEW_COUNT] });
      queryClient.invalidateQueries({ queryKey: [QUERY_ASSET_REVIEW] });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useDeleteAssetReviewLinkMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: AssetReviewControllerDeleteAssetReviewParams) => {
      return await assetReviewDeleteAssetReview(data);
    },
    onMutate: (data) => {
      optimisticListItemRemove<AssetReviewItemDto>({
        queryKey: [QUERY_ASSET_REVIEWS_LIST],
        predicate: (query) => {
          return query.queryKey.includes(QUERY_ASSET_REVIEWS_LIST);
        },
        id: data.reviewId
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_ASSET_REVIEW_COUNT] });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });
  return mutation;
}
